import * as THREE from "three";

// A colour grade on the low-poly surface (IMP-0024): the surface's own colour (its albedo: the palette's vertex
// colours) desaturated or saturated toward its luminance and multiplied by a tint, before the light falls on it. Set
// per hour in the colour script (`DayKey.grade`), so a game can quiet the day's greens and leave the night's, which a
// change of palette can't (the albedo is the same at every hour). A key without a grade has none (saturation 1, a white
// tint): its neighbours' grades fade toward it. The shader skips it under a uniform test while it is the identity, so a
// game that never sets one draws the same pixels.

/** A colour grade: the surface's saturation (1 as authored, 0 grey, above 1 more vivid) and a tint it is multiplied
 *  by (`#rrggbb`, default white). */
export interface ColourGrade {
  saturation?: number;
  tint?: string;
}

/** A grade at one moment, as numbers: saturation and the tint as sRGB 0 to 1. */
export interface GradeFrame {
  saturation: number;
  tint: [number, number, number];
}

/** No grade: the surface as authored. */
export const NO_GRADE: Readonly<GradeFrame> = Object.freeze({ saturation: 1, tint: [1, 1, 1] as [number, number, number] });

/** The grade's uniform on the low-poly surface: (tint r, g, b in linear light, saturation); (1, 1, 1, 1) skips it. */
export interface GradeUniforms {
  uLpGrade: THREE.IUniform<THREE.Vector4>;
}

/** Rec. 709 luminance weights (linear light). */
export const GRADE_LUMA: readonly [number, number, number] = [0.2126, 0.7152, 0.0722];

const lin = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

/** The uniform's value for a grade: the tint in linear light, and the saturation. The identity is (1, 1, 1, 1) exactly. */
export function gradeUniformValue(g: Readonly<GradeFrame>): [number, number, number, number] {
  return [lin(g.tint[0]), lin(g.tint[1]), lin(g.tint[2]), Math.max(0, g.saturation)];
}

/** Write a frame's grade into its uniform (a time-of-day driver every frame; `setLowPolyUniforms`). */
export function setGrade(u: GradeUniforms, g: Readonly<GradeFrame>): void {
  const v = gradeUniformValue(g);
  u.uLpGrade.value.set(v[0], v[1], v[2], v[3]);
}

/** A surface colour (linear light, 0 to 1 each) under the grade: the shader's sum on the CPU, for tests and for
 *  anything that wants to know what a palette colour becomes at an hour. */
export function gradeColour(rgb: readonly [number, number, number], g: Readonly<GradeFrame>): [number, number, number] {
  const [tr, tg, tb, s] = gradeUniformValue(g);
  // the shader skips the identity
  if (tr === 1 && tg === 1 && tb === 1 && s === 1) return [rgb[0], rgb[1], rgb[2]];
  const l = rgb[0] * GRADE_LUMA[0] + rgb[1] * GRADE_LUMA[1] + rgb[2] * GRADE_LUMA[2];
  // GLSL's mix(x, y, a) = x·(1 − a) + y·a
  const m = (c: number, t: number): number => Math.max(0, l * (1 - s) + c * s) * t;
  return [m(rgb[0], tr), m(rgb[1], tg), m(rgb[2], tb)];
}

/** The grade's GLSL: `decl` before the fragment shader's main, `body` right after three's `color_fragment` (where the
 *  vertex colours have been multiplied into `diffuseColor`). */
export const GRADE_GLSL = {
  decl: /* glsl */ `uniform vec4 uLpGrade;\n`,
  body: /* glsl */ `#include <color_fragment>
  if (uLpGrade != vec4(1.0)) {
    // the colour grade (IMP-0024): the surface's own colour toward its luminance by the saturation, then tinted
    float lpL = dot(diffuseColor.rgb, vec3(${GRADE_LUMA.join(", ")}));
    diffuseColor.rgb = max(mix(vec3(lpL), diffuseColor.rgb, uLpGrade.w), 0.0) * uLpGrade.rgb;
  }`,
};
